<template>
  <el-dialog
    title="批量导入手机号账号"
    :visible.sync="dialogVisible"
    width="900px"
    custom-class="phone-import-dialog"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <!-- 操作提示区 -->
    <div class="tips-section">
      <ul>
        <li>单次最多导入 1000 条</li>
        <li>导入为事务模式，任意一行错误全部不保存</li>
        <li>已存在未删除手机号会复用；号码类型冲突则导入失败</li>
      </ul>
    </div>

    <!-- 功能按钮区 -->
    <div class="action-section">
      <el-button type="primary" size="small" @click="downloadTemplate">
        下载导入模板
      </el-button>
      <el-upload
        ref="upload"
        class="upload-btn"
        action="#"
        :auto-upload="false"
        :show-file-list="false"
        :on-change="handleFileChange"
        accept=".xlsx,.xls"
      >
        <el-button type="primary" size="small"> 选择 Excel 文件 </el-button>
      </el-upload>
    </div>

    <!-- 已选择文件信息 -->
    <div v-if="fileName" class="file-info">
      <span>已选择文件：{{ fileName }}</span>
    </div>

    <!-- 预览表格（解析成功后展示） -->
    <div v-if="previewData.length > 0" class="preview-section">
      <h4>预览数据（共 {{ previewData.length }} 条）</h4>
      <el-table
        :data="paginatedPreviewData"
        border
        size="small"
        max-height="300"
        empty-text="暂无数据"
      >
        <el-table-column label="行号" width="60" align="center">
          <template slot-scope="scope">
            {{ previewRowNumber(scope.$index) }}
          </template>
        </el-table-column>
        <el-table-column label="手机号" min-width="120" align="center">
          <template slot-scope="scope">
            {{ scope.row.phone }}
          </template>
        </el-table-column>
        <el-table-column label="合作商id" min-width="140" align="center">
          <template slot-scope="scope">
            {{ scope.row.app_client_id || '-' }}
          </template>
        </el-table-column>
        <el-table-column
          label="合作商名称"
          min-width="140"
          align="center"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            {{ scope.row.app_client_name || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="号码类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.phone_type === 1"
              type="primary"
              size="small"
            >
              真实号码
            </el-tag>
            <el-tag
              v-else-if="scope.row.phone_type === 2"
              type="warning"
              size="small"
            >
              虚拟号码
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          label="备注"
          min-width="140"
          align="center"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="启用状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.is_enabled === 1"
              type="success"
              size="small"
            >
              启用
            </el-tag>
            <el-tag v-else type="info" size="small">禁用</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="previewData.length > previewPageSize" class="preview-pagination">
        <el-pagination
          :current-page="previewPage"
          :page-size="previewPageSize"
          :total="previewData.length"
          :pager-count="5"
          background
          layout="total, prev, pager, next"
          @current-change="previewPage = $event"
        />
      </div>
    </div>

    <!-- 本地校验错误展示区域 -->
    <div v-if="localErrors.length > 0" class="error-section">
      <h4>校验失败（请修正后重新上传）</h4>
      <div class="error-list">
        <p
          v-for="(error, index) in localErrors"
          :key="index"
          class="error-item"
        >
          <span class="error-row">第{{ error.row }}行：</span>
          <span class="error-msg">{{ error.message }}</span>
        </p>
      </div>
    </div>

    <!-- 接口返回错误展示区域 -->
    <div v-if="apiErrors.length > 0" class="error-section">
      <h4>导入失败</h4>
      <div class="error-list">
        <p v-for="(error, index) in apiErrors" :key="index" class="error-item">
          <span class="error-row">第{{ error.row }}行：</span>
          <span class="error-msg">{{ error.message }}</span>
        </p>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        :loading="importLoading"
        :disabled="previewData.length === 0"
        @click="handleImport"
      >
        开始导入
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import request from '@/utils/request'
import * as XLSX from 'xlsx'

export default {
  name: 'PhoneAccountImport',
  props: {
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: false,
    },
    // 合作商列表（用于手机号转合作商名称）
    appClientList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // 弹窗显示状态
      dialogVisible: false,
      // 导入 loading
      importLoading: false,
      // 已选择的文件名
      fileName: '',
      // 解析后的预览数据
      previewData: [],
      previewPage: 1,
      previewPageSize: 20,
      // 本地校验错误列表
      localErrors: [],
      // 接口返回错误列表
      apiErrors: [],
      // 模板列定义（中文表头 -> 字段映射）
      templateColumns: [
        { key: 'phone', label: '手机号', required: true },
        { key: 'app_client_id', label: '合作商ID', required: true },
        { key: 'phone_type', label: '号码类型', required: true },
        { key: 'remark', label: '备注', required: false },
        { key: 'is_enabled', label: '启用状态', required: false },
        { key: 'binding_remark', label: '绑定备注', required: false },
      ],
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
      if (!val) {
        // 关闭时重置数据
        this.resetData()
      }
    },
  },
  computed: {
    paginatedPreviewData() {
      const start = (this.previewPage - 1) * this.previewPageSize
      return this.previewData.slice(start, start + this.previewPageSize)
    },
  },
  methods: {
    previewRowNumber(index) {
      return (this.previewPage - 1) * this.previewPageSize + index + 1
    },

    /**
     * 重置数据
     */
    resetData() {
      this.fileName = ''
      this.previewData = []
      this.previewPage = 1
      this.localErrors = []
      this.apiErrors = []
      this.importLoading = false
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      this.dialogVisible = false
    },

    /**
     * 下载导入模板
     */
    downloadTemplate() {
      // 创建模板数据
      const templateData = [
        {
          手机号: '',
          合作商ID: '',
          号码类型: '请输入数字(1:真实,2:虚拟)',
          备注: '',
          启用状态: '请输入数字(1:启用,0:禁用)',
          绑定备注: '',
        },
      ]

      // 创建工作簿
      const wb = XLSX.utils.book_new()
      // 将数据转换为工作表
      const ws = XLSX.utils.json_to_sheet(templateData)

      // 设置列宽（单位：字符宽度）
      ws['!cols'] = [
        { wch: 20 }, // 手机号
        { wch: 15 }, // 合作商ID
        { wch: 20 }, // 号码类型
        { wch: 25 }, // 备注
        { wch: 20 }, // 启用状态
        { wch: 20 }, // 绑定备注
      ]

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(wb, ws, '导入模板')
      // 下载文件
      XLSX.writeFile(wb, '手机号账号导入模板.xlsx')

      this.$message.success('模板下载成功')
    },

    /**
     * 文件选择变化处理
     */
    handleFileChange(file) {
      // 重置之前的数据
      this.localErrors = []
      this.apiErrors = []
      this.previewData = []
      this.previewPage = 1

      // 校验文件格式
      const fileName = file.name
      const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase()
      if (ext !== '.xlsx' && ext !== '.xls') {
        this.$message.error('只支持 .xlsx 或 .xls 格式的 Excel 文件')
        return
      }

      this.fileName = fileName

      // 读取并解析文件
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary' })
          // 获取第一个工作表
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          // 转换为 JSON 数组
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

          if (jsonData.length <= 1) {
            this.$message.error('Excel 文件内容为空')
            return
          }

          // 解析数据（跳过表头）
          this.parseExcelData(jsonData)
        } catch (error) {
          console.error('解析 Excel 文件失败:', error)
          this.$message.error('解析 Excel 文件失败，请检查文件格式')
        }
      }
      reader.readAsBinaryString(file.raw)
    },

    /**
     * 解析 Excel 数据
     * @param {Array} rawData 原始数据（包含表头）
     */
    parseExcelData(rawData) {
      const errors = []
      const parsedData = []
      const phoneSet = new Set()

      // 获取表头（第一行）
      const headers = rawData[0] || []

      // 字段名映射（支持中文和英文表头）
      const headerMap = {}
      headers.forEach((header, index) => {
        if (header) {
          headerMap[header] = index
        }
      })

      // 检查必填字段是否存在
      const requiredHeaders = ['手机号', '合作商ID', '号码类型']
      for (const rh of requiredHeaders) {
        if (headerMap[rh] === undefined) {
          this.$message.error('Excel 文件缺少必填列：' + rh)
          return
        }
      }

      // 校验总行数（排除表头）
      const dataRows = rawData.length - 1
      if (dataRows > 1000) {
        this.$message.error(
          '单次导入最多支持 1000 条数据，当前文件有 ' + dataRows + ' 条',
        )
        return
      }

      // 遍历数据行（从第二行开始）
      for (let i = 1; i < rawData.length; i++) {
        const row = rawData[i]
        const rowNum = i + 1

        // 跳过空行
        if (!row || row.length === 0 || !row[headerMap['手机号']]) {
          continue
        }

        // 获取字段值
        const phone = String(row[headerMap['手机号']] || '').trim()
        const appClientId = row[headerMap['合作商ID']]
        const phoneType = row[headerMap['号码类型']]
        const remark = String(row[headerMap['备注']] || '').trim()
        const isEnabled = row[headerMap['启用状态']]
        const bindingRemark = String(row[headerMap['绑定备注']] || '').trim()

        // 必填字段校验
        if (!phone) {
          errors.push({ row: rowNum, message: '手机号不能为空' })
          continue
        }

        if (!/^1[3-9]\d{9}$/.test(phone)) {
          errors.push({ row: rowNum, message: '手机号格式不正确：' + phone })
          continue
        }

        if (!appClientId) {
          errors.push({ row: rowNum, message: '合作商ID不能为空' })
          continue
        }

        if (![1, 2].includes(phoneType)) {
          errors.push({ row: rowNum, message: '号码类型只能是 1 或 2' })
          continue
        }

        // 同一文件内重复手机号校验
        if (phoneSet.has(phone)) {
          errors.push({
            row: rowNum,
            message: '同一文件内手机号重复：' + phone,
          })
          continue
        }
        phoneSet.add(phone)

        // is_enabled 处理（默认 1）
        let parsedIsEnabled = 1
        if (isEnabled !== undefined && isEnabled !== null && isEnabled !== '') {
          if (isEnabled === 0 || isEnabled === '0' || isEnabled === '禁用') {
            parsedIsEnabled = 0
          } else if (
            isEnabled === 1 ||
            isEnabled === '1' ||
            isEnabled === '启用'
          ) {
            parsedIsEnabled = 1
          }
        }

        // 获取合作商名称
        const appClient = this.appClientList.find(
          (item) => item.id === appClientId,
        )
        const appClientName = appClient ? appClient.name : ''

        parsedData.push({
          phone: phone,
          app_client_id: Number(appClientId),
          app_client_name: appClientName,
          phone_type: Number(phoneType),
          remark: remark,
          is_enabled: parsedIsEnabled,
          binding_remark: bindingRemark,
        })
      }

      // 如果有校验错误
      if (errors.length > 0) {
        this.localErrors = errors
        this.$message.error(
          '共发现 ' + errors.length + ' 处错误，请修正后重新上传',
        )
        return
      }

      // 如果没有有效数据
      if (parsedData.length === 0) {
        this.$message.error('没有有效的数据行')
        return
      }

      // 解析成功
      this.previewData = parsedData
      this.previewPage = 1
      this.localErrors = []
      this.$message.success('解析成功，共 ' + parsedData.length + ' 条数据')
    },

    /**
     * 提交导入
     */
    handleImport() {
      // 再次校验数据
      if (this.previewData.length === 0) {
        this.$message.error('没有可导入的数据')
        return
      }

      if (this.previewData.length > 1000) {
        this.$message.error('单次导入最多支持 1000 条数据')
        return
      }

      this.importLoading = true
      this.apiErrors = []

      // 构建请求数据
      const items = this.previewData.map((item) => ({
        phone: item.phone,
        app_client_id: item.app_client_id,
        phone_type: item.phone_type,
        remark: item.remark || undefined,
        is_enabled: item.is_enabled,
        binding_remark: item.binding_remark || undefined,
      }))

      // 生成 header 参数
      const timestamp = Date.now()
      // 注意：sign 的生成逻辑需要根据后端规则实现，这里使用占位符
      // 实际项目中需要根据后端提供的签名算法生成
      const sign = this.generateSign(items, timestamp)

      request({
        url: '/api/backend/phone-account-pool/import',
        method: 'post',
        data: { items },
      })
        .then((response) => {
          console.log(response)

          // 成功
          const data = response.data || {}
          this.$message.success(
            '导入成功！共 ' +
              (data.total || 0) +
              ' 行，' +
              '新增号码 ' +
              (data.new_phones || 0) +
              ' 个，' +
              '新增绑定 ' +
              (data.new_bindings || 0) +
              ' 个',
          )
          this.dialogVisible = false
          // 通知父组件刷新列表
          this.$emit('success')
        })
        .catch((error) => {
          console.error('导入失败:', error)
          // error 可能是字符串 'error'（来自 request 拦截器），也可能是 Error 对象
          const resData = error.response ? error.response.data : null
          if (resData && resData.errors && Array.isArray(resData.errors)) {
            this.apiErrors = resData.errors
            this.$message.error(
              '导入失败，共 ' + resData.errors.length + ' 处错误',
            )
          } else if (resData && resData.msg) {
            this.$message.error(resData.msg)
          } else {
            this.$message.error('导入失败')
          }
        })
        .finally(() => {
          this.importLoading = false
        })
    },

    /**
     * 生成签名
     * 注意：此方法需要根据后端实际的签名算法实现
     * 以下为参考实现，实际使用时需要根据后端规则调整
     * @param {Array} items 请求数据
     * @param {Number} timestamp 时间戳
     * @return {String} 签名
     */
    generateSign(items, timestamp) {
      // TODO: 根据后端实际的签名算法实现
      // 参考示例：sign = MD5(appid + timestamp + JSON.stringify(items) + secretKey)
      // const dataStr = JSON.stringify(items)
      // const signStr = `backend${timestamp}${dataStr}your-secret-key`
      // return this.md5(signStr) // 需要安装 crypto-js 或使用其他加密库
      return ''
    },

    /**
     * MD5 加密（需要 crypto-js 库支持）
     * 如果项目未安装 crypto-js，此方法仅作参考
     * @param {String} string 待加密字符串
     * @return {String} 加密后的字符串
     */
    md5(string) {
      // eslint-disable-next-line no-undef
      return crypto.createHash('md5').update(string).digest('hex')
    },
  },
}
</script>

<style scoped>
.tips-section {
  background-color: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 20px;
}

.tips-section ul {
  margin: 0;
  padding-left: 20px;
}

.tips-section li {
  color: #606266;
  font-size: 14px;
  line-height: 1.8;
}

.action-section {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.action-section ::v-deep .el-button + .el-button {
  margin-left: 0;
}

.upload-btn {
  display: inline-block;
}

.file-info {
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #606266;
  line-height: 20px;
  overflow-wrap: anywhere;
}

.preview-section {
  margin-bottom: 15px;
}

.preview-section h4 {
  margin: 0 0 10px 0;
  font-weight: 500;
  font-size: 14px;
  color: #303133;
}

.preview-pagination {
  margin-top: 14px;
  padding-bottom: 2px;
  overflow-x: auto;
  text-align: right;
}

.error-section {
  background-color: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 15px;
}

.error-section h4 {
  margin: 0 0 10px 0;
  font-weight: 500;
  font-size: 14px;
  color: #f56c6c;
}

.error-list {
  max-height: 200px;
  overflow-y: auto;
}

.error-item {
  margin: 5px 0;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.error-row {
  color: #f56c6c;
  font-weight: 500;
}

.error-msg {
  color: #606266;
}

::v-deep .phone-import-dialog {
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 32px);
  max-height: 90vh;
  margin-top: 5vh !important;
}

::v-deep .phone-import-dialog .el-dialog__header {
  flex: none;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #ebeef5;
}

::v-deep .phone-import-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  padding: 20px 24px;
  overflow-y: auto;
}

::v-deep .phone-import-dialog .el-dialog__footer {
  flex: none;
  padding: 14px 24px 18px;
  border-top: 1px solid #ebeef5;
}

@media (max-width: 768px) {
  .tips-section {
    padding: 10px 12px;
  }

  .tips-section ul {
    padding-left: 18px;
  }

  .action-section,
  .action-section .upload-btn,
  .action-section ::v-deep .el-button {
    width: 100%;
  }

  ::v-deep .phone-import-dialog .el-dialog__header,
  ::v-deep .phone-import-dialog .el-dialog__body,
  ::v-deep .phone-import-dialog .el-dialog__footer {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>
